import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Box,
  Button,
  Paper,
  Alert,
  useTheme,
  Skeleton,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PageHeaderActionsBar from './ui/PageHeaderActionsBar';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import AddIcon from '@mui/icons-material/Add';
import { AuthContext } from './AuthContext';
import api from '../api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip, Legend);

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount) || 0);

const emptyBillingState = () => ({
  mrr: 0,
  active_subscriptions: 0,
  ytd_revenue: 0,
  total_outstanding: 0,
  outstanding_by_age: { '1_30': 0, '31_60': 0, '61_90': 0, '91_plus': 0 },
  monthly_revenue: Array(12).fill(0),
  recent_invoices: [],
  raw_outstanding_breakdowns: [],
});

/** Normalize GET /api/billing/dashboard/ into chart + table friendly shape */
function normalizeDashboardResponse(data) {
  const next = emptyBillingState();
  if (!data || typeof data !== 'object') return next;

  next.mrr = parseFloat(data.mrr) || 0;
  next.active_subscriptions = parseInt(data.active_subscriptions, 10) || 0;
  next.ytd_revenue = parseFloat(data.ytd_revenue) || 0;

  const rev = data.revenue;
  if (Array.isArray(rev)) {
    if (rev.length && typeof rev[0] === 'object' && rev[0] !== null && 'month' in rev[0]) {
      rev.forEach((row) => {
        const idx = MONTH_LABELS.indexOf(row.month);
        if (idx >= 0) next.monthly_revenue[idx] = parseFloat(row.revenue) || 0;
      });
    } else if (rev.length && Array.isArray(rev[0])) {
      rev.forEach(([dateStr, value]) => {
        const d = new Date(dateStr);
        if (!Number.isNaN(d.getTime())) {
          const m = d.getMonth();
          next.monthly_revenue[m] += parseFloat(value) || 0;
        }
      });
    }
  }

  const outstanding = data.outstanding || {};
  next.total_outstanding = parseFloat(outstanding.total) || 0;
  const breakdowns = Array.isArray(outstanding.breakdowns) ? outstanding.breakdowns : [];
  next.raw_outstanding_breakdowns = breakdowns.map((b) => ({
    label: b.label || '',
    value: parseFloat(b.value) || 0,
  }));

  breakdowns.forEach((b) => {
    const label = (b.label || '').toLowerCase();
    const v = parseFloat(b.value) || 0;
    if (label.includes('1-30') || label.includes('1–30')) next.outstanding_by_age['1_30'] += v;
    else if (label.includes('31-60') || label.includes('31–60')) next.outstanding_by_age['31_60'] += v;
    else if (label.includes('61-90') || label.includes('61–90')) next.outstanding_by_age['61_90'] += v;
    else if (label.includes('91')) next.outstanding_by_age['91_plus'] += v;
  });

  let invoices = Array.isArray(data.invoices) ? [...data.invoices] : [];
  invoices.sort((a, b) => {
    const aid = a.id != null ? a.id : 0;
    const bid = b.id != null ? b.id : 0;
    return bid - aid;
  });
  next.recent_invoices = invoices.slice(0, 25);

  return next;
}

/** Data Grid v7+ may pass raw value or { value }. */
const cellVal = (p) => {
  if (p == null) return p;
  if (typeof p === 'object' && 'value' in p) return p.value;
  return p;
};

function statusChipProps(status) {
  const s = (status || '').toString().toUpperCase();
  if (s === 'DONE' || s === 'PAID') return { label: status || 'Paid', color: 'success' };
  if (s === 'PENDING' || s === 'SENT' || s === 'OVERDUE') return { label: status || 'Open', color: 'warning' };
  if (s === 'DRAFT') return { label: status || 'Draft', color: 'default' };
  if (s === 'CANCELLED' || s === 'VOID') return { label: status || 'Cancelled', color: 'error' };
  return { label: status || '—', color: 'default' };
}

/** Inline KPI card: number is near-black, only the sub-label carries color. Lifts on hover. */
const KpiCard = ({ label, value, sublabel, sublabelColor = 'text.secondary', loading }) => (
  <Paper
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      transition: 'transform 160ms ease, box-shadow 160ms ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
      },
      height: '100%',
    }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
      {label}
    </Typography>
    {loading ? (
      <Skeleton width="60%" height={36} />
    ) : (
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: '#111827', // near-black for all 4 cards
          lineHeight: 1.1,
          mb: 0.5,
        }}
      >
        {value}
      </Typography>
    )}
    {loading ? (
      <Skeleton width="40%" height={18} />
    ) : (
      <Typography variant="caption" sx={{ color: sublabelColor, fontWeight: 500 }}>
        {sublabel}
      </Typography>
    )}
  </Paper>
);

/** SVG empty-state illustration for the YTD Revenue panel. */
const EmptyRevenueIllustration = () => (
  <Box sx={{ width: 140, height: 90, mb: 1.5, color: 'text.disabled' }}>
    <svg viewBox="0 0 140 90" width="140" height="90" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="8" y="12" width="124" height="66" rx="6" stroke="currentColor" strokeWidth="1.5" />
      <line x1="8" y1="66" x2="132" y2="66" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" />
      <line x1="8" y1="50" x2="132" y2="50" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" />
      <line x1="8" y1="34" x2="132" y2="34" stroke="currentColor" strokeWidth="1" strokeDasharray="3 3" />
      <path d="M12 66 L40 66 L68 66 L96 66 L128 66" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="70" cy="66" r="3" fill="currentColor" />
    </svg>
  </Box>
);

const BillingDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userPermissions } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingData, setBillingData] = useState(() => emptyBillingState());

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/billing/dashboard/');
      setBillingData(normalizeDashboardResponse(response.data));
      setError(null);
    } catch {
      setError('Failed to load dashboard data');
      setBillingData(emptyBillingState());
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async () => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        const userData = localStorage.getItem('user');
        if (userData) {
          const u = JSON.parse(userData);
          userId = u.id;
        }
      }

      if (!userId) {
        setError('User not logged in');
        setLoading(false);
        return;
      }

      await api.get(`/api/multi-tenant/users/${userId}/`);
      await fetchDashboardData();
    } catch {
      await fetchDashboardData();
    }
  };

  useEffect(() => {
    setLoading(true);
    if (!userPermissions || Object.keys(userPermissions).length === 0) {
      fetchUserPermissions();
      return;
    }

    if (!userPermissions?.can_view_invoices) {
      setError('No access to billing features.');
      setLoading(false);
      return;
    }

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPermissions]);

  const primaryMain = theme.palette.primary.main;

  const revenueChartData = {
    labels: MONTH_LABELS,
    datasets: [
      {
        label: 'YTD Revenue',
        data: billingData.monthly_revenue,
        borderColor: primaryMain,
        backgroundColor: alpha(primaryMain, 0.1),
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const doughnutBreakdowns =
    billingData.raw_outstanding_breakdowns.length > 0
      ? billingData.raw_outstanding_breakdowns
      : [
          { label: '1-30 Days', value: billingData.outstanding_by_age['1_30'] },
          { label: '31-60 Days', value: billingData.outstanding_by_age['31_60'] },
          { label: '61-90 Days', value: billingData.outstanding_by_age['61_90'] },
          { label: '91+ Days', value: billingData.outstanding_by_age['91_plus'] },
        ];

  const chartRows = doughnutBreakdowns.filter((b) => b.value > 0);
  const doughnutLabels = chartRows.length ? chartRows.map((b) => b.label) : ['No outstanding'];
  const doughnutValues = chartRows.length ? chartRows.map((b) => b.value) : [1];

  const ytdFromMonthly = billingData.monthly_revenue.reduce((a, b) => a + b, 0);
  const ytdDisplay = billingData.ytd_revenue > 0 ? billingData.ytd_revenue : ytdFromMonthly;

  // True when the YTD line chart would be a flat $0 line.
  const hasNoRevenue = !loading && ytdDisplay === 0 && billingData.monthly_revenue.every((v) => v === 0);

  const isOpenInvoice = (inv) => {
    const s = (inv.status || '').toString().toUpperCase();
    return !['PAID', 'DONE', 'VOID', 'CANCELLED'].includes(s);
  };
  const openInvCount = billingData.recent_invoices.filter(isOpenInvoice).length;
  const overdueCount = billingData.recent_invoices.filter(
    (inv) => (inv.status || '').toString().toUpperCase() === 'OVERDUE'
  ).length;

  const startOfToday = (() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  })();
  const endOfWeek = new Date(startOfToday);
  endOfWeek.setDate(endOfWeek.getDate() + 7);
  const dueThisWeekCount = billingData.recent_invoices.filter((inv) => {
    if (!isOpenInvoice(inv)) return false;
    const raw = inv.due_date || inv.dueDate;
    if (!raw) return false;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return false;
    return d >= startOfToday && d <= endOfWeek;
  }).length;

  const { outstanding_by_age: oba } = billingData;
  const t60 = oba['31_60'] || 0;
  const t90 = oba['61_90'] || 0;
  const t91 = oba['91_plus'] || 0;
  const pastDueTotal = t60 + t90 + t91;

  const mr = billingData.monthly_revenue;
  const curMonth = new Date().getMonth();
  const thisMonthRev = mr[curMonth] || 0;
  const priorMonthRev = curMonth > 0 ? mr[curMonth - 1] || 0 : mr[11] || 0;
  const monthDelta = thisMonthRev - priorMonthRev;

  // "$0 vs prior month" style sublabel for Payments Collected.
  const paymentsSublabel = loading
    ? ''
    : `${monthDelta >= 0 ? '' : '−'}${formatCurrency(Math.abs(monthDelta))} vs prior month`;

  const invoiceRows = billingData.recent_invoices.map((invoice) => ({
    id: invoice.id,
    invoiceNumber: invoice.invoice_number ?? invoice.invoiceNumber ?? '—',
    client: invoice.client_name || invoice.client || 'Unknown',
    amount: invoice.total != null ? Number(invoice.total) : 0,
    dueDate: invoice.due_date || invoice.dueDate || '—',
    status: invoice.status || 'Draft',
  }));

  const columns = [
    { field: 'invoiceNumber', headerName: 'Invoice #', width: 130 },
    { field: 'client', headerName: 'Client', flex: 1, minWidth: 180 },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (p) => formatCurrency(cellVal(p)),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const { label, color } = statusChipProps(params.value);
        return <Chip label={label} color={color} size="small" variant="outlined" />;
      },
    },
    {
      field: 'dueDate',
      headerName: 'Due date',
      width: 130,
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: () => (
        <Button variant="outlined" size="small" onClick={() => navigate('/billing/invoices')}>
          View
        </Button>
      ),
    },
  ];

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <PageHeaderActionsBar
        title="Billing Dashboard"
        description="Overview of revenue, outstanding invoices, and recent activity"
        primaryAction={{ label: 'Create Invoice', onClick: () => navigate('/billing/invoices'), icon: <AddIcon /> }}
      />

      {/* KPI cards — numbers near-black, color lives on sub-labels only. */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Outstanding Balance"
            value={formatCurrency(billingData.total_outstanding)}
            sublabel={`${openInvCount} open invoice${openInvCount === 1 ? '' : 's'}`}
            sublabelColor="text.secondary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Past Due"
            value={formatCurrency(pastDueTotal)}
            sublabel={`${overdueCount} overdue`}
            sublabelColor="error.main"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Payments Collected"
            value={formatCurrency(ytdDisplay)}
            sublabel={paymentsSublabel}
            sublabelColor="text.secondary"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Open Invoices"
            value={String(openInvCount)}
            sublabel={`${dueThisWeekCount} due this week`}
            sublabelColor="warning.main"
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2 }}>
              Recurring Revenue
            </Typography>
            {loading ? (
              <>
                <Skeleton width="40%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="60%" height={40} sx={{ mb: 2 }} />
                <Skeleton width="50%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="35%" height={32} />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  MRR
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(billingData.mrr)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>
                  Active subscriptions
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {billingData.active_subscriptions}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} sm={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2 }}>
              YTD Revenue
            </Typography>
            <Box sx={{ height: 220 }}>
              {loading ? (
                <Skeleton variant="rounded" width="100%" height={220} />
              ) : hasNoRevenue ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 2,
                  }}
                >
                  <EmptyRevenueIllustration />
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    No payments received yet this year
                  </Typography>
                </Box>
              ) : (
                <Line
                  data={revenueChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'top' } },
                    scales: {
                      y: { beginAtZero: true },
                    },
                  }}
                />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mt: 2 }}>
              {loading ? <Skeleton width={140} /> : <>Total: {formatCurrency(ytdDisplay)}</>}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', mb: 2 }}>
              Outstanding by Age
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 180px', minWidth: 180, maxWidth: 220 }}>
                {loading ? (
                  <Skeleton variant="circular" width={180} height={180} sx={{ mx: 'auto' }} />
                ) : (
                  <Doughnut
                    data={{
                      labels: doughnutLabels,
                      datasets: [
                        {
                          data: doughnutValues,
                          backgroundColor: chartRows.length
                            ? [
                                primaryMain,
                                alpha(primaryMain, 0.8),
                                alpha(primaryMain, 0.6),
                                alpha(primaryMain, 0.4),
                              ].slice(0, doughnutLabels.length)
                            : [theme.palette.action.disabled],
                          borderWidth: 0,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: { legend: { position: 'top' } },
                    }}
                  />
                )}
              </Box>
              <Box sx={{ flex: '1 1 180px' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Total Outstanding
                </Typography>
                {loading ? (
                  <Skeleton width={120} height={36} />
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {formatCurrency(billingData.total_outstanding)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper
            sx={{
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Outstanding Invoices
              </Typography>
            </Box>
            <Box sx={{ height: 400 }}>
              {loading ? (
                <Box sx={{ p: 2 }}>
                  <Skeleton height={40} sx={{ mb: 1 }} />
                  <Skeleton height={40} sx={{ mb: 1 }} />
                  <Skeleton height={40} sx={{ mb: 1 }} />
                  <Skeleton height={40} />
                </Box>
              ) : (
                <DataGrid
                  rows={invoiceRows}
                  columns={columns}
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  slots={{ toolbar: GridToolbar }}
                  disableRowSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-cell': { borderColor: 'divider' },
                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'action.hover' },
                  }}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BillingDashboard;